import React from 'react';
import { format } from 'date-fns';
import { ManagedUser } from '../../types/auth.types';
import { UserRole } from '../../types/organization.types';
import { useUserRoles, useUpdateUser } from '../../hooks/useUserQueries';
import { useToast } from '../../hooks/useToast';
import { LedgerSkeleton } from '../shell/LedgerSkeleton';
import { cn } from '../../lib/cn';

// Same date rules as RegisterPeek/TaskPeek: local time for timestamps.
const stampLocal = (iso: string | null | undefined): string | null =>
  iso ? format(new Date(iso), 'MMM d, yyyy').toUpperCase() : null;

interface UserPeekProps {
  user: ManagedUser;
  canAdmin: boolean;
  // True when the peeked user is the viewer: hides the Active switch, since
  // self-deactivation locks the account out at the next token refresh.
  isSelf: boolean;
  onClose: () => void;
  onEditName: (user: ManagedUser) => void;
  onAddRole: (user: ManagedUser) => void;
  onEditRole: (role: UserRole) => void;
  onRemoveRole: (role: UserRole) => void;
  onSetDefaults: (user: ManagedUser) => void;
}

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <div className="cap-label">{label}</div>
    <div className="mt-0.5 text-scale-1 text-ink">{children}</div>
  </div>
);

const DefaultLine: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex items-baseline justify-between border-b border-rule-faint py-1.5 text-scale-1">
    <span className="cap-label">{label}</span>
    <span className="text-ink">{value}</span>
  </div>
);

export const UserPeek: React.FC<UserPeekProps> = ({
  user,
  canAdmin,
  isSelf,
  onClose,
  onEditName,
  onAddRole,
  onEditRole,
  onRemoveRole,
  onSetDefaults,
}) => {
  const roles = useUserRoles(user.id);
  const update = useUpdateUser();
  const toast = useToast();

  const onSaveError = () => toast.error('Save failed');
  const recordedStamp = stampLocal(user.createdAt);
  const updatedStamp = stampLocal(user.updatedAt);

  return (
    <aside aria-label="User peek" className="border-l border-ink bg-paper-raised p-5">
      <div className="flex items-baseline justify-between">
        <span className="cap-label">USER</span>
        <span className="flex gap-3">
          {canAdmin && (
            <button
              type="button"
              onClick={() => onEditName(user)}
              className="cap-label cursor-pointer !text-accent hover:underline"
            >
              Edit name
            </button>
          )}
          <button
            type="button"
            aria-label="Close peek"
            onClick={onClose}
            className="cursor-pointer text-scale-0 text-ink-muted hover:text-ink"
          >
            ✕
          </button>
        </span>
      </div>

      <h2 className="mt-1.5 font-serif text-scale-4 font-medium text-ink">{user.fullName}</h2>
      <p className="mb-3 break-all font-mono text-scale-1 text-ink-muted">{user.email}</p>
      <div className="border-b border-ink" />

      <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 py-3">
        <Field label="Status">
          {canAdmin && !isSelf ? (
            <button
              type="button"
              role="switch"
              aria-checked={user.isActive}
              aria-label="Active"
              disabled={update.isPending}
              onClick={() =>
                update.mutate({ id: user.id, data: { isActive: !user.isActive } }, { onError: onSaveError })
              }
              className="flex cursor-pointer items-center gap-2 disabled:cursor-default"
            >
              <span aria-hidden className={cn('h-3 w-3 border border-ink', user.isActive && 'bg-ink')} />
              <span className="text-scale-1 text-ink">{user.isActive ? 'Active' : 'Inactive'}</span>
            </button>
          ) : (
            // Display-only by design: non-admins get read-only status, and so
            // does an admin on their own row (see the isSelf prop).
            <span className="flex items-center gap-2">
              <span aria-hidden className={cn('h-2.5 w-2.5 border border-ink', user.isActive && 'bg-ink')} />
              <span className="text-scale-1 text-ink">{user.isActive ? 'Active' : 'Inactive'}</span>
            </span>
          )}
        </Field>
      </div>

      <div className="cap-label mt-4 border-b border-ink pb-0.5">Role assignments</div>
      {roles.isPending ? (
        <LedgerSkeleton lines={3} className="mt-2" />
      ) : (roles.data ?? []).length === 0 ? (
        <p className="pt-1.5 font-serif text-scale-1 italic text-ink-muted">No roles assigned.</p>
      ) : (
        // Dim while a background refetch is in flight so rows made stale by a
        // just-fired mutation don't read as current.
        <ul className={cn(roles.isFetching && 'opacity-60')}>
          {(roles.data ?? []).map((role) => (
            <li
              key={role.id}
              className="flex items-baseline justify-between border-b border-rule-faint py-1.5 text-scale-1"
            >
              <span className="flex items-baseline gap-2">
                <span className="cap-label">{role.role}</span>
                <span className="text-ink">{role.organizationName}</span>
              </span>
              {canAdmin && (
                <span className="flex gap-3">
                  <button
                    type="button"
                    aria-label={`Edit ${role.organizationName} role`}
                    onClick={() => onEditRole(role)}
                    className="cap-label cursor-pointer !text-accent hover:underline"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    aria-label={`Remove ${role.organizationName} role`}
                    onClick={() => onRemoveRole(role)}
                    className="cap-label cursor-pointer text-ink-muted hover:!text-sev-critical"
                  >
                    Remove
                  </button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {canAdmin && (
        <button
          type="button"
          onClick={() => onAddRole(user)}
          className="cap-label mt-1.5 inline-block cursor-pointer !text-accent hover:underline"
        >
          + Add role
        </button>
      )}

      <div className="cap-label mt-4 border-b border-ink pb-0.5">Defaults</div>
      <div className="pt-0.5">
        <DefaultLine label="Company" value={user.defaultCompanyName || '—'} />
        {user.defaultTeamName && <DefaultLine label="Team" value={user.defaultTeamName} />}
        {user.defaultDepartmentName && <DefaultLine label="Department" value={user.defaultDepartmentName} />}
      </div>
      {canAdmin && (
        <button
          type="button"
          onClick={() => onSetDefaults(user)}
          className="cap-label mt-1.5 inline-block cursor-pointer !text-accent hover:underline"
        >
          Set defaults ↗
        </button>
      )}

      <div className="cap-label mt-4 border-b border-ink pb-0.5">History</div>
      <div className="pt-1.5 font-serif text-scale-1 italic text-ink-muted">
        {recordedStamp && <div>Recorded {recordedStamp}</div>}
        {updatedStamp && <div>Updated {updatedStamp}</div>}
      </div>
    </aside>
  );
};
